import React from 'react';
import { ClientBrand } from '../data/clients';

interface ClientLogoProps {
  client: ClientBrand;
  showDetails?: boolean;
  showCategoryBadge?: boolean;
}

export function ClientLogo({ client, showDetails = false, showCategoryBadge = false }: ClientLogoProps) {
  // Custom brand logo graphic designs with SVG icons & typography
  const renderLogoGraphic = () => {
    if (client.logoUrl) {
      return (
        <img
          src={client.logoUrl}
          alt={`${client.name} logo`}
          className="max-h-10 max-w-full object-contain"
          loading="lazy"
        />
      );
    }

    switch (client.id) {
      case 'charles-tyrwhitt':
        return (
          <div className="flex items-center gap-1.5 font-serif min-w-0 max-w-full">
            <span className="font-extrabold text-[11px] sm:text-xs tracking-wider text-[#0b1c30] uppercase truncate">
              CHARLES TYRWHITT
            </span>
          </div>
        );

      case 'charles-keith':
        return (
          <div className="flex items-center gap-1 font-sans min-w-0 max-w-full">
            <span className="font-light text-[11px] sm:text-xs tracking-[0.18em] text-gray-900 uppercase truncate">
              CHARLES &amp; KEITH
            </span>
          </div>
        );

      case 'brilliant-trade':
        return (
          <div className="flex items-center gap-2 min-w-0 max-w-full">
            <div className="w-5 h-5 rounded-md bg-[#0059bb] text-white flex items-center justify-center font-black text-[10px] shrink-0">
              B
            </div>
            <div className="flex flex-col text-left leading-none min-w-0">
              <span className="font-extrabold text-[11px] sm:text-xs text-[#0b1c30] tracking-tight truncate">BRILLIANT</span>
              <span className="font-bold text-[8px] sm:text-[9px] text-[#0059bb] tracking-widest uppercase truncate">TRADE</span>
            </div>
          </div>
        );

      case 'mayfield-lighting':
        return (
          <div className="flex items-center gap-1.5 font-display min-w-0 max-w-full">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-[#7c3aed] shrink-0">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor" opacity="0.2"/>
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="font-bold text-[11px] sm:text-xs tracking-wide text-gray-900 truncate">
              MAYFIELD
            </span>
          </div>
        );

      case 'mercator':
        return (
          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
            <div className="w-3.5 h-3.5 rounded-full bg-gradient-to-tr from-sky-600 to-blue-400 flex items-center justify-center shrink-0">
              <div className="w-1 h-1 rounded-full bg-white"></div>
            </div>
            <span className="font-bold text-[11px] sm:text-xs tracking-wider text-gray-900 uppercase truncate">
              MERCATOR
            </span>
          </div>
        );

      case 'verve-lighting':
        return (
          <div className="flex items-center gap-1.5 font-sans min-w-0 max-w-full">
            <span className="font-black text-xs sm:text-sm italic text-[#2563eb] tracking-tighter shrink-0">
              VERVE
            </span>
            <span className="text-[9px] text-gray-500 font-semibold tracking-widest uppercase truncate">LIGHTING</span>
          </div>
        );

      case 'brilliant-lighting':
        return (
          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
            <div className="w-3.5 h-3.5 rounded-xs bg-[#ea580c] flex items-center justify-center rotate-45 shrink-0">
              <div className="w-1 h-1 bg-white -rotate-45"></div>
            </div>
            <span className="font-extrabold text-[11px] sm:text-xs text-gray-900 tracking-tight truncate">
              Brilliant
            </span>
          </div>
        );

      case 'arlec':
        return (
          <div className="flex items-center gap-1 min-w-0 max-w-full">
            <div className="bg-[#dc2626] text-white px-2 py-0.5 rounded font-black text-[11px] tracking-wider uppercase">
              ARLEC
            </div>
          </div>
        );

      case 'grid-connect':
        return (
          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
            <div className="w-4 h-4 rounded-md bg-[#059669] flex items-center justify-center text-white text-[9px] font-bold shrink-0">
              G
            </div>
            <span className="font-bold text-[11px] sm:text-xs text-gray-900 tracking-tight truncate">
              Grid<span className="text-[#047857]">Connect</span>
            </span>
          </div>
        );

      case 'davis-lighting':
        return (
          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
            <span className="font-black text-[11px] sm:text-xs text-[#a16207] tracking-widest uppercase shrink-0">
              DAVIS
            </span>
            <span className="text-[9px] text-gray-600 font-bold uppercase truncate">LIGHTING</span>
          </div>
        );

      case 'antsig':
        return (
          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
            <div className="w-2.5 h-2.5 bg-[#4f46e5] rounded-full ring-2 ring-indigo-200 shrink-0"></div>
            <span className="font-extrabold text-[11px] sm:text-xs text-[#0b1c30] tracking-wider uppercase truncate">
              ANTSIG
            </span>
          </div>
        );

      case 'orion-security':
        return (
          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
            <div className="w-4 h-4 rounded bg-[#0f172a] text-white flex items-center justify-center text-[9px] font-black shrink-0">
              O
            </div>
            <span className="font-extrabold text-[11px] sm:text-xs text-gray-900 tracking-tight truncate">
              ORION <span className="text-gray-500 font-semibold text-[9px]">SEC</span>
            </span>
          </div>
        );

      default:
        return (
          <span className="font-bold text-xs text-gray-900 truncate">{client.name}</span>
        );
    }
  };

  return (
    <div className="h-full flex flex-col justify-between p-3.5 sm:p-4 bg-white hover:bg-gray-50/80 rounded-2xl border border-gray-200 hover:border-[#0059bb] transition-all group shadow-2xs overflow-hidden w-full">
      {/* Brand logo container - always uncluttered and centered */}
      <div className="flex items-center justify-center h-10 w-full min-w-0 px-1">
        {renderLogoGraphic()}
      </div>

      {/* Category badge - placed clearly beneath the logo, never overlapping */}
      {showCategoryBadge && (
        <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-center min-w-0">
          <span className="text-[9px] sm:text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 truncate max-w-full uppercase tracking-wider text-center">
            {client.category}
          </span>
        </div>
      )}

      {/* Optional details (for large project grid cards) */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 w-full">
          {!showCategoryBadge && (
            <div className="flex items-center justify-between gap-1 mb-1 min-w-0">
              <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-[#0059bb] truncate uppercase tracking-wider">
                {client.category}
              </span>
            </div>
          )}
          <p className="text-[11px] text-gray-600 line-clamp-2 leading-relaxed break-words">
            {client.shortDescription}
          </p>
          <div className="flex items-center justify-between pt-1 gap-2 min-w-0">
            <span className="text-[11px] font-semibold text-[#0059bb] truncate break-all block flex-1">
              {client.domain}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
